<script setup lang="ts">
import { BannerIntro } from "@/models/Banner";

const profile = useProfileStore();
const store = useBigHeroBannerStore();

const props = defineProps<{
  index: number;
  noTransition?: boolean;
}>();
const banner = computed<BannerIntro>(() => {
  return store.banners[props.index] as BannerIntro;
});

const tabIndex = computed(() =>
  store.currentSlideIndex === props.index ? "0" : "-1",
);

const emit = defineEmits<{
  (e: "load"): void;
}>();

function onLoad() {
  emit("load");
}

const internalLink = computed(
  () => /^\//.test(banner.value.url) || /^\.\.\//.test(banner.value.url),
);
</script>

<template>
  <BigHeroBannerSlide :index="index || 0">
    <div class="big-hero-banner-intro">
      <BigHeroBannerBackgroundImg
        :img="banner.img"
        :alt="$t('SV.LIT_BackgroundImageToIntro')"
        :first-image="index === 0"
        :no-lazy="true"
        :no-transition="noTransition"
        @load="onLoad"
      />
      <div class="inner-block">
        <h1 class="title">
          {{ banner.text.title }}
        </h1>

        <div class="subtitle">
          {{ banner.text.subtitle }}
        </div>
        <div class="items">
          <div>
            <FAIcon
              class="fa-kit fa-ccli-lyrics"
              aria-hidden="true"
            />{{ banner.text.lyrics }}
          </div>
          <div>
            <FAIcon
              class="fa-kit fa-ccli-chord"
              aria-hidden="true"
            />{{ banner.text.chord }}
          </div>

          <div>
            <FAIcon
              class="fa-kit fa-ccli-lead"
              aria-hidden="true"
            />{{ banner.text.lead }}
          </div>
          <div>
            <FAIcon
              class="fa-kit fa-ccli-vocal"
              aria-hidden="true"
            />{{ banner.text.vocal }}
          </div>
        </div>
        <div>
          <Button
            :to="(internalLink && banner.url) || undefined"
            :href="(!internalLink && banner.url) || undefined"
            variant="transparent-white"
            class="hero-banner-feature-button"
            :class="{ hidden: !profile.profile || profile.profile.signedIn }"
            :tabindex="tabIndex"
            :data-name="'INTRO: ' + banner.text.title"
            icon-start="fa-solid fa-user-plus text-primary text-white"
          >
            {{ banner.text.cta }}
          </Button>
        </div>
      </div>
    </div>
  </BigHeroBannerSlide>
</template>

<style scoped lang="postcss">
.hero-banner-feature-button.hidden {
  display: none !important;
}
.big-hero-banner-intro {
  @apply px-5 text-center text-light-100;
}
h1 {
  @apply font-display text-3xl font-bold text-light-100 md:text-5xl;
}
.inner-block {
  @apply grid gap-2;
}
.subtitle {
  @apply text-2xl;
}

.fa-kit {
  @apply mr-2 text-xl text-primary;
}

.items {
  @apply my-2 block flex-wrap items-center gap-5 text-lg md:text-xl;
}

.items > div {
  @apply mx-4 my-1 inline-flex whitespace-nowrap;
}
</style>
